if (!customElements.get('flipzy-routine-offer')) {
  class FlipzyRoutineOffer extends HTMLElement {
    async connectedCallback() {
      if (!this.priceRecovery) {
        this.priceRecovery = () => this.connectedCallback();
        document.addEventListener('flipzy:price-test-recovered', this.priceRecovery);
      }
      if (this.initialized || this.initializing) return this.initializing;
      this.initializing = this.initialize();
      try { await this.initializing; }
      finally { this.initializing = null; }
    }

    async initialize() {
      if (window.FlipzyPriceTestBoot?.blocked) return;
      if (this.initialized) return;

      const dataElement = this.querySelector('[data-flipzy-routine-offer-data]');
      if (!dataElement) return;

      try {
        this.data = JSON.parse(dataElement.textContent);
      } catch (error) {
        console.error(error);
        return;
      }

      if (window.FlipzyPriceTest) {
        await window.FlipzyPriceTest.ready;
        if (this.isConnected === false) return;
        if (window.FlipzyPriceTest.state.blocked && !window.FlipzyPriceTest.state.recoverableDiscount) return;
        try {
          this.data.board.price = window.FlipzyPriceTest.unitPrice('base', this.data.board.price);
          this.data.packs.forEach((pack) => { pack.price = window.FlipzyPriceTest.unitPrice('pack', pack.price); });
        } catch (error) { return; }
      }
      this.initialized = true;
      this.boardQuantity = this.clamp(Number(this.data.defaultBoardQuantity || 1), 1, 4);
      this.packMap = new Map(this.data.packs.map((pack) => [pack.id, pack]));
      this.packVariantMap = new Map(
        this.data.packs.filter((pack) => pack.variantId).map((pack) => [String(pack.variantId), pack.id])
      );
      this.selectedPackIds = this.getDefaultPackIds(this.data.promotionEnabled ? this.boardQuantity : 1);
      this.ensureIncludedRoutineSlots();
      this.committedSignature = null;
      this.committedCartPrice = null;
      this.isSubmitting = false;
      this.hasInteracted = false;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundHandlePointerDown = this.handlePointerDown.bind(this);
      this.boundHandlePointerMove = this.handlePointerMove.bind(this);
      this.boundHandlePointerEnd = this.handlePointerEnd.bind(this);
      this.boundHandleStripChange = this.handleStripChange.bind(this);
      this.boundHandleDisclosure = this.handleDisclosure.bind(this);

      this.addEventListener('click', this.boundHandleClick);
      this.addEventListener('keydown', this.boundHandleKeydown);
      this.addEventListener('pointerdown', this.boundHandlePointerDown);
      this.addEventListener('pointermove', this.boundHandlePointerMove);
      this.addEventListener('pointerup', this.boundHandlePointerEnd);
      this.addEventListener('pointercancel', this.boundHandlePointerEnd);
      this.addEventListener('scroll', this.boundHandleStripChange, true);
      this.addEventListener('load', this.boundHandleStripChange, true);
      this.addEventListener('toggle', this.boundHandleDisclosure, true);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.syncCommittedCartState());
      }
      this.render();
      this.dataset.enhanced = 'true';
      this.initializeRoutineStrips();
      this.syncCommittedCartState();
    }

    disconnectedCallback() {
      document.removeEventListener('flipzy:price-test-recovered', this.priceRecovery);
      this.priceRecovery = null;
      if (!this.initialized) return;
      this.removeEventListener('click', this.boundHandleClick);
      this.removeEventListener('keydown', this.boundHandleKeydown);
      this.removeEventListener('pointerdown', this.boundHandlePointerDown);
      this.removeEventListener('pointermove', this.boundHandlePointerMove);
      this.removeEventListener('pointerup', this.boundHandlePointerEnd);
      this.removeEventListener('pointercancel', this.boundHandlePointerEnd);
      this.removeEventListener('scroll', this.boundHandleStripChange, true);
      this.removeEventListener('load', this.boundHandleStripChange, true);
      this.removeEventListener('toggle', this.boundHandleDisclosure, true);
      this.routineStripObserver?.disconnect();
      this.cartUpdateUnsubscriber?.();
      this.initialized = false;
      delete this.dataset.enhanced;
    }

    handleClick(event) {
      if (this.isSubmitting) return;
      const scrollControl = event.target.closest('[data-routine-scroll]');
      if (scrollControl) {
        const strip = scrollControl.closest('.flipzy-routine-option__art-shell')?.querySelector('[data-routine-strip]');
        if (strip) this.scrollRoutineStrip(strip, Number(scrollControl.dataset.routineScroll));
        return;
      }

      const boardOption = event.target.closest('[data-board-option], [data-sticky-board-option]');
      if (boardOption) {
        this.hasInteracted = true;
        this.setBoardQuantity(
          Number(boardOption.dataset.boardOption || boardOption.dataset.stickyBoardOption),
        );
        this.clearStatus();
        this.render();
        return;
      }

      const routineOption = event.target.closest('[data-routine-change]');
      if (routineOption && !routineOption.disabled) {
        const card = routineOption.closest('[data-routine-card]');
        const wasFocused = document.activeElement === routineOption;
        this.hasInteracted = true;
        this.changeRoutineQuantity(card.dataset.routineCard,
          Number(routineOption.dataset.routineChange));
        this.clearStatus();
        this.render();
        if (wasFocused && routineOption.closest('[hidden]')) {
          const nextControl = this.getPackQuantity(card.dataset.routineCard)
            ? card.querySelector('[data-routine-quantity-controls] [data-routine-change="1"]')
            : card.querySelector('[data-routine-add]');
          nextControl.focus({ preventScroll: true });
        }
        return;
      }

      const addButton = event.target.closest('[data-add-offer]');
      if (addButton) this.submitOffer(addButton);
    }

    handleDisclosure(event) {
      if (!event.target.matches('[data-more-routines]') || !event.target.open) return;
      requestAnimationFrame(() => this.refreshRoutineStrips(event.target));
    }

    refreshRoutineStrips(root = this) {
      root.querySelectorAll('[data-routine-strip]').forEach((strip) => {
        if (!strip.clientWidth) return;
        if (!strip.dataset.initialized) {
          const itemCount = strip.querySelectorAll('[data-routine-art-item]').length;
          const configuredIndex = Number(strip.dataset.initialIndex);
          const initialIndex = strip.dataset.initialIndex !== undefined
            && Number.isInteger(configuredIndex) && configuredIndex >= 0 && configuredIndex < itemCount
            ? configuredIndex
            : Math.floor((itemCount - 1) / 2);
          this.centerRoutineItem(strip, initialIndex, 'auto');
          strip.dataset.initialized = 'true';
        }
        this.updateRoutineStripState(strip);
      });
    }

    initializeRoutineStrips() {
      const strips = Array.from(this.querySelectorAll('[data-routine-strip]'));

      if (typeof ResizeObserver === 'function') {
        this.routineStripObserver = new ResizeObserver((entries) => {
          entries.forEach((entry) => this.refreshRoutineStrips(entry.target.parentElement));
        });
        strips.forEach((strip) => this.routineStripObserver.observe(strip));
      }

      requestAnimationFrame(() => this.refreshRoutineStrips());
    }

    handleKeydown(event) {
      const strip = event.target.closest('[data-routine-strip]');
      if (!strip || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;

      event.preventDefault();
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      this.scrollRoutineStrip(strip, direction);
    }

    scrollRoutineStrip(strip, direction) {
      const currentIndex = this.getCenteredRoutineIndex(strip);
      const itemCount = strip.querySelectorAll('[data-routine-art-item]').length;
      const targetIndex = this.clamp(currentIndex + direction, 0, itemCount - 1);

      this.centerRoutineItem(strip, targetIndex, this.reducedMotionQuery.matches ? 'auto' : 'smooth');
    }

    handlePointerDown(event) {
      const strip = event.target.closest('[data-routine-strip]');
      if (!strip) return;

      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.routineDrag = {
        strip,
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: strip.scrollLeft,
      };
      strip.setPointerCapture(event.pointerId);
      strip.classList.add('is-dragging');
      event.preventDefault();
    }

    handlePointerMove(event) {
      if (!this.routineDrag || this.routineDrag.pointerId !== event.pointerId) return;

      const distance = event.clientX - this.routineDrag.startX;
      this.routineDrag.strip.scrollLeft = this.routineDrag.startScrollLeft - distance;
      event.preventDefault();
    }

    handlePointerEnd(event) {
      if (!this.routineDrag || this.routineDrag.pointerId !== event.pointerId) return;

      const { strip, pointerId } = this.routineDrag;
      if (strip.hasPointerCapture(pointerId)) strip.releasePointerCapture(pointerId);
      strip.classList.remove('is-dragging');
      this.routineDrag = null;

      requestAnimationFrame(() => {
        this.centerRoutineItem(
          strip,
          this.getCenteredRoutineIndex(strip),
          this.reducedMotionQuery.matches ? 'auto' : 'smooth'
        );
      });
    }

    handleStripChange(event) {
      const strip = event.target.closest?.('[data-routine-strip]');
      if (strip) this.updateRoutineStripState(strip);
    }

    updateRoutineStripState(strip) {
      const card = strip.closest('[data-routine-card]');
      if (!card) return;

      const maximumScroll = Math.max(0, strip.scrollWidth - strip.clientWidth);
      const centeredIndex = this.getCenteredRoutineIndex(strip);
      const items = strip.querySelectorAll('[data-routine-art-item]');
      const canScrollLeft = maximumScroll > 0 && centeredIndex > 0;
      const canScrollRight = maximumScroll > 0 && centeredIndex < items.length - 1;
      const previousControl = card.querySelector('[data-routine-scroll="-1"]');
      const nextControl = card.querySelector('[data-routine-scroll="1"]');

      items.forEach((item, index) => item.classList.toggle('is-centered', index === centeredIndex));
      strip.dataset.centeredIndex = String(centeredIndex);

      card.classList.toggle('can-scroll-left', canScrollLeft);
      card.classList.toggle('can-scroll-right', canScrollRight);
      if (previousControl) previousControl.disabled = !canScrollLeft;
      if (nextControl) nextControl.disabled = !canScrollRight;
    }

    getCenteredRoutineIndex(strip) {
      const items = Array.from(strip.querySelectorAll('[data-routine-art-item]'));
      if (!items.length) return 0;

      const viewportCenter = strip.scrollLeft + strip.clientWidth / 2;
      let centeredIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      items.forEach((item, index) => {
        const itemCenter = item.offsetLeft + item.offsetWidth / 2;
        const distance = Math.abs(itemCenter - viewportCenter);
        if (distance >= closestDistance) return;
        closestDistance = distance;
        centeredIndex = index;
      });

      return centeredIndex;
    }

    centerRoutineItem(strip, index, behavior = 'smooth') {
      const items = strip.querySelectorAll('[data-routine-art-item]');
      const item = items[index];
      if (!item) return;

      strip.scrollTo({
        left: item.offsetLeft - (strip.clientWidth - item.offsetWidth) / 2,
        behavior,
      });
    }

    changeRoutineQuantity(packId, change) {
      const pack = this.packMap.get(packId);
      if (!pack) return;
      if (change < 0) {
        const index = this.selectedPackIds.lastIndexOf(packId);
        if (index !== -1) this.selectedPackIds.splice(index, 1);
      } else if (pack.available && pack.variantId && this.getPackQuantity(packId) < 99) {
        this.selectedPackIds.push(packId);
      }
    }

    getPackQuantity(packId) {
      return this.selectedPackIds.filter((id) => id === packId).length;
    }

    setBoardQuantity(quantity) {
      const nextQuantity = this.clamp(quantity, 1, 4);
      this.boardQuantity = nextQuantity;
      this.ensureIncludedRoutineSlots();
    }

    ensureIncludedRoutineSlots() {
      if (!this.data.promotionEnabled) return;
      const availableIds = this.getDefaultPackIds(this.packMap.size);
      if (!availableIds.length) return;
      // Fill only unused included slots; retain chosen packs and duplicate quantities.
      while (this.selectedPackIds.length < this.boardQuantity) {
        const nextId = availableIds.find((id) => !this.selectedPackIds.includes(id)) || availableIds[0];
        this.selectedPackIds.push(nextId);
      }
    }

    getDefaultPackIds(count) {
      return this.data.defaultPackOrder
        .filter((packId) => {
          const pack = this.packMap.get(packId);
          return pack && pack.available && pack.variantId;
        })
        .slice(0, this.clamp(count, 0, this.packMap.size));
    }

    getSelectedPacks() {
      return this.selectedPackIds.map((packId) => this.packMap.get(packId)).filter(Boolean);
    }

    getTotals(boardQuantity = this.boardQuantity, selectedPacks = this.getSelectedPacks()) {
      const boardPrice = Number(this.data.board.price || 0);
      const boardComparePrice = this.getCompareAtUnitPrice(this.data.board);
      const discountPercent = this.clamp(Number(this.data.additionalBoardDiscountPercent || 0), 0, 100);
      const additionalBoardPrice = this.data.promotionEnabled
        ? Math.round(boardPrice * (1 - discountPercent / 100))
        : boardPrice;
      const volume = window.FlipzyOfferPricing.baseTotals(
        boardPrice, boardQuantity, this.data.currencyCode || 'EUR', this.data.baseVolumeDiscounts === true
      );
      const boardTotal = this.data.baseVolumeDiscounts
        ? volume.total
        : boardPrice + Math.max(0, boardQuantity - 1) * additionalBoardPrice;
      const includedCount = this.data.promotionEnabled ? Math.min(boardQuantity, selectedPacks.length) : 0;
      // The Shopify Function includes the lowest-priced eligible packs first.
      // Keep the shopper's selection order intact while pricing the same allocation.
      const pricedPacks = [...selectedPacks].sort((left, right) => Number(left.price || 0) - Number(right.price || 0));
      const includedPacks = pricedPacks.slice(0, includedCount);
      const extraPacks = pricedPacks.slice(includedCount);
      const extraPacksTotal = extraPacks.reduce((total, pack) => total + Number(pack.price || 0), 0);
      const compareTotal =
        boardComparePrice * boardQuantity +
        selectedPacks.reduce((total, pack) => total + this.getCompareAtUnitPrice(pack), 0);

      return {
        additionalBoardPrice,
        boardDiscountPercent: volume.percent,
        boardCompareTotal: boardComparePrice * boardQuantity,
        boardTotal,
        includedCount,
        includedPacks,
        extraPacks,
        extraPacksTotal,
        total: boardTotal + extraPacksTotal,
        compareTotal,
      };
    }

    getCompareAtUnitPrice(product) {
      const price = Number(product?.price || 0);
      const compareAtPrice = Number(product?.compareAtPrice || 0);
      return compareAtPrice > price ? compareAtPrice : price;
    }

    render() {
      const selectedPacks = this.getSelectedPacks();
      const totals = this.getTotals(this.boardQuantity, selectedPacks);

      this.renderBoardOptions();
      this.renderBoardSelection();
      this.renderRoutineOptions(selectedPacks, totals);
      this.revealSelectedRoutines(selectedPacks);
      this.renderStepHeaders(selectedPacks, totals);
      this.renderSelection(selectedPacks, totals);
      this.renderStickyBuilder(selectedPacks, totals);
      this.renderButton(selectedPacks, totals);
    }

    revealSelectedRoutines(selectedPacks) {
      const disclosure = this.querySelector('[data-more-routines]');
      if (!disclosure) return;
      const suggestedIds = this.data.defaultPackOrder.slice(0, this.data.suggestedPackCount ?? 12);
      if (selectedPacks.some((pack) => !suggestedIds.includes(pack.id))) disclosure.open = true;
    }

    renderBoardOptions() {
      this.querySelectorAll('[data-board-option]').forEach((button) => {
        const quantity = Number(button.dataset.boardOption);
        const isSelected = quantity === this.boardQuantity;
        const totals = this.getTotals(quantity, []);

        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
        this.setText('[data-board-option-price]', this.formatMoney(totals.boardTotal), button);
        this.setText('[data-board-option-discount]', totals.boardDiscountPercent
          ? this.formatLabel('percentOff', { percent: totals.boardDiscountPercent }, `${totals.boardDiscountPercent}%`) : '', button);
        const bestPrice = button.querySelector('[data-board-option-best]');
        if (bestPrice) bestPrice.hidden = totals.boardDiscountPercent === 0;
      });

      this.querySelectorAll('[data-sticky-board-option]').forEach((button) => {
        const isSelected = Number(button.dataset.stickyBoardOption) === this.boardQuantity;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });
    }

    renderBoardSelection() {
      const totals = this.getTotals(this.boardQuantity, []);
      const boardLabel = this.quantityLabel(this.boardQuantity, this.data.labels.boardSingular, this.data.labels.boardPlural, 'board');
      const routineLabel = this.quantityLabel(this.boardQuantity, this.data.labels.routineSingular, this.data.labels.routinePlural, 'routine');
      this.querySelectorAll('[data-board-preview]').forEach((preview) => {
        preview.hidden = Number(preview.dataset.boardPreview) !== this.boardQuantity;
      });
      // A restored cart can exceed the four quantities offered by these controls.
      const previewStage = this.querySelector('[data-board-preview-stage]');
      if (previewStage) previewStage.hidden = this.boardQuantity > 4;
      this.setText('[data-board-selection-heading]', `${this.boardQuantity} ${boardLabel}`);
      this.setText('[data-board-selection-price]', this.formatMoney(totals.boardTotal));
      this.setText('[data-board-selection-included]', this.formatLabel(
        'includedCount', { count: this.boardQuantity, routine: routineLabel },
        `${this.boardQuantity} ${routineLabel} ${this.data.labels.included}`
      ));
      const included = this.querySelector('[data-board-selection-included]');
      if (included) included.hidden = !this.data.promotionEnabled;
      const hasDiscount = totals.boardDiscountPercent > 0;
      const hasFreeShipping = this.hasFreeShipping(this.getDisplayedTotal(this.getTotals().total));
      this.setText('[data-board-selection-discount]', hasDiscount
        ? this.formatLabel('percentOff', { percent: totals.boardDiscountPercent }, `${totals.boardDiscountPercent}%`) : '');
      this.setText('[data-board-selection-compare]', hasDiscount ? this.formatMoney(totals.boardCompareTotal) : '');
      const benefits = this.querySelector('[data-board-selection-benefits]');
      const discount = this.querySelector('[data-board-selection-discount]');
      const compare = this.querySelector('[data-board-selection-compare]');
      const shipping = this.querySelector('[data-board-selection-shipping]');
      if (benefits) benefits.hidden = !hasDiscount && !hasFreeShipping;
      if (discount) discount.hidden = !hasDiscount;
      if (compare) compare.hidden = !hasDiscount;
      if (shipping) shipping.hidden = !hasFreeShipping;
    }

    renderRoutineOptions(selectedPacks, totals) {
      this.querySelectorAll('[data-routine-card]').forEach((card) => {
        const pack = this.packMap.get(card.dataset.routineCard);
        if (!pack) return;
        const quantity = this.getPackQuantity(pack.id);
        const canAdd = pack.available && pack.variantId && quantity < 99;
        const price = card.querySelector('[data-routine-price]');
        card.classList.toggle('is-selected', quantity > 0);
        card.querySelector('[data-routine-add]').hidden = quantity > 0;
        card.querySelector('[data-routine-quantity-controls]').hidden = quantity === 0;
        card.querySelectorAll('[data-routine-change="1"]').forEach((button) => { button.disabled = !canAdd; });
        card.querySelector('[data-routine-change="-1"]').disabled = quantity === 0;
        this.setText('[data-routine-quantity]', quantity, card);

        if (price) {
          const nextIncluded = this.data.promotionEnabled && selectedPacks.length < this.boardQuantity;
          const additionalPrice = this.formatMoney(
            this.getTotals(this.boardQuantity, [...selectedPacks, pack]).total - totals.total
          );
          price.classList.toggle('is-included', nextIncluded);
          price.textContent = !pack.available || !pack.variantId
            ? this.data.labels.unavailablePack
            : nextIncluded ? this.data.labels.nextIncluded
              : this.formatLabel('additionalPackPrice', { price: additionalPrice }, additionalPrice);
        }
      });
    }

    renderStepHeaders(selectedPacks, totals) {
      const boardLabel = this.quantityLabel(
        this.boardQuantity,
        this.data.labels.boardSingular,
        this.data.labels.boardPlural,
        'board'
      );
      const routineLabel = this.quantityLabel(
        selectedPacks.length,
        this.data.labels.routineSingular,
        this.data.labels.routinePlural,
        'routine'
      );

      this.setText('[data-board-step-summary]', this.formatLabel(
        'boardSelected', { count: this.boardQuantity, board: boardLabel },
        `${this.boardQuantity} ${boardLabel} ${this.data.labels.selected}`
      ));
      const includedProgress = this.formatLabel(
        'includedProgress', { selected: totals.includedCount, count: this.boardQuantity },
        `${totals.includedCount} / ${this.boardQuantity}`
      );
      const extraSummary = this.formatLabel(
        'extraSummary', { count: totals.extraPacks.length, price: this.formatMoney(totals.extraPacksTotal) },
        `${totals.extraPacks.length} ${this.data.labels.extra} (${this.formatMoney(totals.extraPacksTotal)})`
      );
      this.setText(
        '[data-routine-step-summary]',
        this.data.promotionEnabled
          ? `${includedProgress}${totals.extraPacks.length ? ` · ${extraSummary}` : ''}`
          : this.formatLabel('routineSelected', { count: selectedPacks.length, routine: routineLabel }, `${selectedPacks.length} ${routineLabel} ${this.data.labels.selected}`)
      );
      const packPrices = [...new Set(this.data.packs.filter((pack) => pack.available && pack.variantId).map((pack) => Number(pack.price)))];
      const extraHelp = packPrices.length === 1
        ? this.formatLabel('extraRoutineHelp', { price: this.formatMoney(packPrices[0]) }, this.data.labels.variedRoutineHelp)
        : this.data.labels.variedRoutineHelp;
      this.setText('[data-routine-included-help]', this.data.promotionEnabled ? this.data.labels.includedRoutineHelp : '');
      this.setText('[data-routine-pricing-help]', extraHelp);
      this.setText('[data-running-total]', `= ${this.formatMoney(this.getDisplayedTotal(totals.total))}`);
    }

    renderSelection(selectedPacks, totals) {
      const boardLabel = this.quantityLabel(
        this.boardQuantity,
        this.data.labels.boardSingular,
        this.data.labels.boardPlural,
        'board'
      );
      const routineLabel = this.quantityLabel(
        selectedPacks.length,
        this.data.labels.routineSingular,
        this.data.labels.routinePlural,
        'routine'
      );
      const heading = `${this.boardQuantity} ${boardLabel} + ${selectedPacks.length} ${routineLabel}`;

      this.setText('[data-selection-heading]', heading);
      const displayedTotal = this.getDisplayedTotal(totals.total);
      this.setText('[data-selection-total]', this.formatMoney(displayedTotal));
      const selectionMath = this.querySelector('[data-selection-math]');
      if (selectionMath) selectionMath.hidden = Math.abs(displayedTotal - totals.total) > 1;
      this.setText('[data-board-math-price]', this.formatMoney(totals.boardTotal));

      const boardMath = totals.boardDiscountPercent
        ? `${this.boardQuantity} ${boardLabel} · ${this.formatLabel('percentOff', { percent: totals.boardDiscountPercent }, `${totals.boardDiscountPercent}%`)}`
        : !this.data.baseVolumeDiscounts && this.data.promotionEnabled && this.boardQuantity > 1 && Number(this.data.additionalBoardDiscountPercent) > 0
          ? `${this.boardQuantity} ${boardLabel} (${this.formatMoney(this.data.board.price)} + ${this.boardQuantity - 1} × ${this.formatMoney(totals.additionalBoardPrice)})`
          : `${this.boardQuantity} ${boardLabel}`;
      this.setText('[data-board-math-label]', boardMath);

      const includedRow = this.querySelector('[data-included-math-row]');
      if (includedRow) includedRow.hidden = totals.includedCount === 0;
      const includedRoutineLabel = this.quantityLabel(totals.includedCount, this.data.labels.routineSingular, this.data.labels.routinePlural, 'routine');
      this.setText('[data-included-math-label]', this.formatLabel(
        'includedCount', { count: totals.includedCount, routine: includedRoutineLabel },
        `${totals.includedCount} ${includedRoutineLabel} ${this.data.labels.included}`
      ));

      const extraRow = this.querySelector('[data-extra-math-row]');
      if (extraRow) extraRow.hidden = totals.extraPacks.length === 0;
      const extraRoutineLabel = this.quantityLabel(totals.extraPacks.length, this.data.labels.routineSingular, this.data.labels.routinePlural, 'routine');
      this.setText('[data-extra-math-label]', this.formatLabel(
        'extraCount', { count: totals.extraPacks.length, routine: extraRoutineLabel },
        `${totals.extraPacks.length} ${this.data.labels.extra} ${extraRoutineLabel}`
      ));
      this.setText('[data-extra-math-price]', this.formatMoney(totals.extraPacksTotal));

      this.renderSelectedRoutineNames(selectedPacks, totals);

      const shipping = this.querySelector('[data-selection-shipping]');
      if (shipping) shipping.hidden = !this.hasFreeShipping(displayedTotal);

      const saving = this.querySelector('[data-selection-saving]');
      const hasSaving = displayedTotal === totals.total && totals.compareTotal > totals.total;
      if (saving) saving.hidden = !hasSaving;
      this.setText('[data-selection-compare]', hasSaving ? this.formatMoney(totals.compareTotal) : '');
      this.setText(
        '[data-selection-save]',
        hasSaving ? this.formatLabel(
          'saveAmount', { price: this.formatMoney(totals.compareTotal - totals.total) },
          `${this.data.labels.save} ${this.formatMoney(totals.compareTotal - totals.total)}`
        ) : ''
      );
    }

    renderSelectedRoutineNames(selectedPacks, totals) {
      const container = this.querySelector('[data-selection-routines]');
      if (!container) return;

      const fragment = document.createDocumentFragment();
      [...new Map(selectedPacks.map((pack) => [pack.id, pack])).values()].forEach((pack) => {
        const item = document.createElement('span');
        item.className = 'flipzy-selection__routine';
        item.textContent = `${this.getPackQuantity(pack.id)} × ${pack.title}`;
        fragment.append(item);
      });
      if (!selectedPacks.length) {
        const prompt = document.createElement('span');
        prompt.textContent = this.data.labels.chooseRoutine;
        fragment.append(prompt);
      }

      container.replaceChildren(fragment);
    }

    renderStickyBuilder(selectedPacks, totals) {
      const stickyBuilder = this.querySelector('[data-sticky-builder]');
      if (!stickyBuilder) return;

      const boardLabel = this.quantityLabel(
        this.boardQuantity,
        this.data.labels.boardSingular,
        this.data.labels.boardPlural,
        'board'
      );
      const routineLabel = this.quantityLabel(
        selectedPacks.length,
        this.data.labels.routineSingular,
        this.data.labels.routinePlural,
        'routine'
      );
      const includedRoutineLabel = this.quantityLabel(
        totals.includedCount,
        this.data.labels.routineSingular,
        this.data.labels.routinePlural,
        'routine'
      );

      this.setText(
        '[data-sticky-selection]',
        `${this.boardQuantity}× ${boardLabel} + ${selectedPacks.length} ${routineLabel}`,
        stickyBuilder
      );
      this.setText(
        '[data-sticky-included]',
        this.formatLabel('includedCount', { count: totals.includedCount, routine: includedRoutineLabel }, `${totals.includedCount} ${includedRoutineLabel} ${this.data.labels.included}`),
        stickyBuilder
      );
      this.setText('[data-sticky-total]', this.formatMoney(this.getDisplayedTotal(totals.total)), stickyBuilder);
    }

    renderButton(selectedPacks, totals) {
      const buttons = Array.from(this.querySelectorAll('[data-add-offer]'));
      if (!buttons.length || this.isSubmitting) return;

      const validationMessage = this.getValidationMessage(selectedPacks);
      const signature = this.getSelectionSignature();
      const isInCart = Boolean(this.committedSignature) && signature === this.committedSignature;
      const needsUpdate = Boolean(this.committedSignature) && !isInCart;

      buttons.forEach((button) => {
        button.disabled = Boolean(validationMessage) && !isInCart;
        button.classList.toggle('is-in-cart', isInCart);

        if (isInCart) {
          this.setText('[data-add-label]', this.data.labels.openCart, button);
          this.setText('[data-add-price]', '', button);
        } else if (!selectedPacks.length) {
          this.setText('[data-add-label]', this.data.labels.chooseRoutine, button);
          this.setText('[data-add-price]', '', button);
        } else {
          this.setText('[data-add-label]', needsUpdate ? this.data.labels.updateCart : this.data.labels.addToCart, button);
          this.setText('[data-add-price]', `· ${this.formatMoney(totals.total)}`, button);
        }
      });

      if (validationMessage && selectedPacks.length && !isInCart) this.showStatus(validationMessage);
    }

    getValidationMessage(selectedPacks) {
      if (!selectedPacks.length) return this.data.labels.chooseRoutine;
      if (!this.data.board.variantId) return this.data.labels.missingProducts;
      if (!this.data.board.available) return this.data.labels.soldOut;
      if (selectedPacks.some((pack) => !pack.variantId)) return this.data.labels.missingProducts;
      if (selectedPacks.some((pack) => !pack.available)) return this.data.labels.soldOut;
      return '';
    }

    getSelectionSignature() {
      return `${this.boardQuantity}|${this.selectedPackIds.join(',')}`;
    }

    hasFreeShipping(subtotal) {
      const currency = this.data.currencyCode || 'EUR';
      const shopifyCurrency = window.Shopify?.currency;
      const rate = shopifyCurrency?.active === currency ? shopifyCurrency.rate : null;
      const observed = this.committedCartShipping?.signature === this.getSelectionSignature()
        ? this.committedCartShipping : null;
      return window.FlipzyOfferPricing.freeShipping(
        observed?.baseQuantity ?? this.boardQuantity, observed?.subtotal ?? subtotal,
        currency, this.data.shopCurrency || 'EUR', rate
      );
    }

    getDisplayedTotal(expectedTotal) {
      return this.committedCartPrice?.signature === this.getSelectionSignature()
        ? this.committedCartPrice.total
        : expectedTotal;
    }

    quantityLabel(quantity, singular, plural, kind) {
      const count = Number(quantity);
      const fallback = count === 1 ? singular : plural;
      const forms = this.data.quantityForms?.[kind];
      if (!forms || !Number.isSafeInteger(count) || count < 0) return fallback;

      try {
        const locale = this.data.quantityLocale || document.documentElement.lang || 'en';
        if (!this.quantityPluralRules || this.quantityPluralLocale !== locale) {
          this.quantityPluralRules = new Intl.PluralRules(locale, { type: 'cardinal' });
          this.quantityPluralLocale = locale;
        }
        const category = count === 0 ? 'zero' : this.quantityPluralRules.select(count);
        const label = forms[category];
        return typeof label === 'string' && label.trim() && !/translation missing/i.test(label)
          ? label : fallback;
      } catch (error) {
        return fallback;
      }
    }

    formatLabel(key, values, fallback = '') {
      const template = this.data.labels[key];
      if (typeof template !== 'string' || !template) return fallback;

      const used = new Set();
      let invalid = false;
      // Shopify inserts the sentinel values after translating its {{ variable }}
      // placeholders. Single braces and [percent] keep existing merchant labels
      // compatible; translated or missing tokens must never hide a real price.
      const result = template.replace(
        /__FLIPZY_([A-Z]+)__|\{\{\s*([^{}]+?)\s*\}\}|\{([^{}]+)\}|\[([a-z]+)\]/g,
        (match, sentinel, liquid, legacy, bracket) => {
          const name = (sentinel || liquid || legacy || bracket).trim().toLowerCase();
          if (!Object.prototype.hasOwnProperty.call(values, name)) {
            invalid = true;
            return match;
          }
          used.add(name);
          return String(values[name]);
        }
      );
      return invalid || Object.keys(values).some((name) => !used.has(name)) ? fallback : result;
    }

    clamp(value, minimum, maximum) {
      return Math.max(minimum, Math.min(maximum, Number(value) || 0));
    }

    formatMoney(cents) {
      const currencyCode = this.data.currencyCode || 'EUR';
      const amount = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: currencyCode,
        }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)} ${currencyCode}`.trim();
      }
    }

    setText(selector, text = '', root = this) {
      const element = root.querySelector(selector);
      if (element) element.textContent = text;
    }

    clearStatus() {
      this.querySelectorAll('[data-offer-status], [data-sticky-status]').forEach((status) => {
        status.hidden = true;
        status.textContent = '';
      });
    }

    showStatus(message) {
      this.querySelectorAll('[data-offer-status], [data-sticky-status]').forEach((status) => {
        status.hidden = !message;
        status.textContent = message || '';
      });
    }

    setBusy(button, isUpdating, label) {
      this.isSubmitting = true;
      this.querySelectorAll('[data-add-offer]').forEach((action) => {
        action.disabled = true;
        action.classList.add('loading');
        action.setAttribute('aria-busy', 'true');
        this.setText('[data-add-label]', label || (isUpdating ? this.data.labels.updating : this.data.labels.adding), action);
        this.setText('[data-add-price]', '', action);
        action.querySelector('.loading__spinner')?.classList.remove('hidden');
      });
    }

    clearBusy(button) {
      this.isSubmitting = false;
      this.querySelectorAll('[data-add-offer]').forEach((action) => {
        action.classList.remove('loading');
        action.removeAttribute('aria-busy');
        action.querySelector('.loading__spinner')?.classList.add('hidden');
      });
    }

    buildCartItems(selectedPacks, totals, signature, revision) {
      const boardLabel = this.quantityLabel(
        this.boardQuantity,
        this.data.labels.boardSingular,
        this.data.labels.boardPlural,
        'board'
      );
      const routineLabel = this.quantityLabel(
        selectedPacks.length,
        this.data.labels.routineSingular,
        this.data.labels.routinePlural,
        'routine'
      );
      const privateProperties = {
        ...(window.FlipzyPriceTest?.properties() || {}),
        _flipzy_offer_id: this.data.offerId,
        _flipzy_offer_signature: signature,
        _flipzy_offer_revision: revision,
        _flipzy_offer_promo: String(Boolean(this.data.promotionEnabled)),
      };
      const routineQuantities = new Map();
      selectedPacks.forEach((pack) => {
        const variantId = Number(pack.variantId);
        const routine = routineQuantities.get(variantId);
        if (routine) routine.quantity += 1;
        else routineQuantities.set(variantId, { pack, quantity: 1 });
      });
      const boardProperties = {
        ...privateProperties,
        _flipzy_offer_role: 'board',
        '_Flipzy offer': `${this.boardQuantity} ${boardLabel} + ${selectedPacks.length} ${routineLabel}`,
        '_Selected routines': Array.from(routineQuantities.values())
          .map(({ pack, quantity }) => quantity > 1 ? `${pack.title} ×${quantity}` : pack.title)
          .join(', '),
      };

      const items = [
        {
          id: Number(this.data.board.variantId),
          quantity: this.boardQuantity,
          properties: boardProperties,
        },
      ];

      routineQuantities.forEach(({ pack, quantity }) => {
        items.push({
          id: Number(pack.variantId),
          quantity,
          properties: {
            ...privateProperties,
            _flipzy_offer_role: 'routine',
          },
        });
      });

      return items;
    }

    async syncCommittedCartState() {
      try {
        const cart = await this.fetchCart();
        const offerItems = this.getExistingOfferItems(cart);
        this.committedSignature = this.getCartSelectionSignature(offerItems);
        if (this.committedSignature && !this.hasInteracted) this.applySelectionSignature(this.committedSignature);
        this.observeCartPrice(cart, this.committedSignature);
        if (this.committedSignature === this.getSelectionSignature()) {
          const warning = this.getCartPriceWarning(cart, this.getTotals().total);
          if (warning) this.showStatus(warning);
        }
        this.render();
      } catch (error) {
        // The offer remains usable if the optional initial cart-state check fails.
      }
    }

    async fetchCart() {
      return this.requestCart(`${window.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
    }

    async requestCart(url, options) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || this.data.labels.error);
        return result;
      } catch (error) {
        // An interrupted write can still have reached Shopify. Read the cart
        // before another attempt; never automatically replay an add request.
        const failure = new Error(error.name === 'AbortError' ? this.data.labels.error : error.message || this.data.labels.error);
        failure.uncertainCart = options?.method === 'POST';
        throw failure;
      } finally { clearTimeout(timer); }
    }

    getExistingOfferItems(cart) {
      return cart.items.filter(
        (item) => item.properties && item.properties._flipzy_offer_id === this.data.offerId
      );
    }

    getCartSelectionSignature(items) {
      const boardItems = items.filter((item) => item.properties?._flipzy_offer_role === 'board');
      if (!boardItems.length || boardItems.some((item) =>
        Number(item.variant_id || item.id) !== Number(this.data.board.variantId)
      )) return null;
      const boardQuantity = boardItems.reduce((quantity, item) => quantity + Number(item.quantity || 0), 0);
      if (!Number.isInteger(boardQuantity) || boardQuantity < 1) return null;

      const savedSignature = boardItems[0].properties?._flipzy_offer_signature || '';
      const savedPackIds = String(savedSignature).split('|')[1]?.split(',').filter(Boolean) || [];
      const remainingQuantities = new Map();
      items.forEach((item) => {
        if (item.properties?._flipzy_offer_role !== 'routine') return;
        const packId = this.packVariantMap.get(String(item.variant_id || item.id));
        const quantity = Number(item.quantity || 0);
        if (!packId || !Number.isInteger(quantity) || quantity < 1) return;
        remainingQuantities.set(packId, (remainingQuantities.get(packId) || 0) + quantity);
      });
      const orderedPackIds = [];
      // Retain the shopper's order, but let actual cart quantities decide how
      // many copies remain after cart edits or Shopify discount line splits.
      savedPackIds.forEach((packId) => {
        const remaining = remainingQuantities.get(packId) || 0;
        if (!remaining) return;
        orderedPackIds.push(packId);
        remainingQuantities.set(packId, remaining - 1);
      });
      remainingQuantities.forEach((quantity, packId) => {
        for (let index = 0; index < quantity; index += 1) orderedPackIds.push(packId);
      });

      return `${boardQuantity}|${orderedPackIds.join(',')}`;
    }

    applySelectionSignature(signature) {
      const [quantityValue, packValue = ''] = String(signature).split('|');
      const quantity = Number(quantityValue);
      const packIds = packValue.split(',').filter((packId) => this.packMap.has(packId));
      if (!Number.isInteger(quantity) || quantity < 1) return;

      this.boardQuantity = quantity;
      this.selectedPackIds = packIds;
    }

    buildRequestConfig(body) {
      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify(body);
      return config;
    }

    addSectionRendering(body, cartDrawer) {
      if (!cartDrawer) return;
      body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    async postCartAdd(items, cartDrawer = null) {
      const body = { items };
      this.addSectionRendering(body, cartDrawer);
      return this.requestCart(window.routes.cart_add_url, this.buildRequestConfig(body));
    }

    async updateCartQuantities(updates, cartDrawer) {
      const body = { updates };
      this.addSectionRendering(body, cartDrawer);
      return this.requestCart(window.routes.cart_update_url, this.buildRequestConfig(body));
    }

    async updateOfferItems(items, cart, cartDrawer, onChange) {
      const desired = new Map(items.map((item) => [String(item.id), item]));
      const currentQuantities = new Map();
      this.getExistingOfferItems(cart).forEach((item) => {
        const id = String(item.variant_id || item.id);
        currentQuantities.set(id, (currentQuantities.get(id) || 0) + Number(item.quantity));
      });
      const additions = items.map((item) => ({
        ...item, quantity: Math.max(0, item.quantity - (currentQuantities.get(String(item.id)) || 0)),
      })).filter((item) => item.quantity > 0);
      // Add only positive differences first. A sold-out new pack must not
      // remove the existing selection, and retained Bases need no extra stock.
      if (additions.length) {
        await this.postCartAdd(additions);
        onChange();
        cart = await this.fetchCart();
      }

      const remaining = new Map(items.map((item) => [String(item.id), item.quantity]));
      const reductions = {};
      this.getExistingOfferItems(cart).forEach((item) => {
        const id = String(item.variant_id || item.id);
        const retained = Math.min(Number(item.quantity), remaining.get(id) || 0);
        remaining.set(id, Math.max(0, (remaining.get(id) || 0) - retained));
        if (retained !== Number(item.quantity)) reductions[item.key] = retained;
      });
      if (Object.keys(reductions).length) {
        cart = await this.updateCartQuantities(reductions, cartDrawer);
        onChange();
      }

      // Property changes and discount allocation can merge lines or change
      // their keys. Use the current response for each following line change.
      const changedKeys = new Set();
      let stale;
      while ((stale = this.getExistingOfferItems(cart).find((item) => {
        const target = desired.get(String(item.variant_id || item.id));
        return target && item.properties?._flipzy_offer_revision !== target.properties._flipzy_offer_revision;
      }))) {
        if (changedKeys.has(stale.key)) throw new Error(this.data.labels.cartPriceChanged);
        changedKeys.add(stale.key);
        const target = desired.get(String(stale.variant_id || stale.id));
        const body = {
          id: stale.key,
          quantity: Number(stale.quantity),
          properties: { ...stale.properties, ...target.properties },
        };
        this.addSectionRendering(body, cartDrawer);
        const changed = await this.requestCart(window.routes.cart_change_url, this.buildRequestConfig(body));
        cart = changed;
        onChange();
      }
      if (cartDrawer && !cart.sections) cart.sections = await this.fetchCartSections(cartDrawer);
      return cart;
    }

    getPromotionCodes(cart) {
      const codes = (cart.discount_codes || []).map((discount) =>
        String(typeof discount === 'string' ? discount : discount?.code || '').trim()
      ).filter(Boolean);
      const promotionCode = String(this.data.promotionCode || '').trim();
      if (this.data.baseVolumeDiscounts) {
        return [...new Set(codes.filter((code) => code.toLowerCase() !== promotionCode.toLowerCase()))];
      }
      if (promotionCode && !codes.some((code) => code.toLowerCase() === promotionCode.toLowerCase())) {
        codes.push(promotionCode);
      }
      return [...new Set(codes)];
    }

    async applyPromotion(cart, cartDrawer) {
      if (!this.data.promotionEnabled || !String(this.data.promotionCode || '').trim()) return null;
      const codes = this.getPromotionCodes(cart);
      if (this.data.baseVolumeDiscounts && codes.length === (cart.discount_codes || []).length) return null;
      const body = { discount: codes.join(',') };
      this.addSectionRendering(body, cartDrawer);
      return this.requestCart(window.routes.cart_update_url, this.buildRequestConfig(body));
    }

    async fetchCartSections(cartDrawer) {
      if (!cartDrawer) return null;
      const sectionIds = cartDrawer.getSectionsToRender().map((section) => section.id);
      return this.requestCart(`${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
    }

    async resolveOfferCart(parsedState, cartDrawer) {
      let promotionFailed = false;
      if (this.data.promotionEnabled && String(this.data.promotionCode || '').trim()) {
        try {
          parsedState = await this.applyPromotion(await this.fetchCart(), cartDrawer) || parsedState;
        } catch (error) {
          promotionFailed = true;
        }
      }
      const cart = await this.fetchCart();
      const sections = promotionFailed
        ? await this.fetchCartSections(cartDrawer)
        : parsedState?.sections;
      return { ...cart, sections, promotionFailed };
    }

    getActualOfferTotal(cart) {
      const items = this.getExistingOfferItems(cart);
      if (!items.length || items.some((item) => item.final_line_price == null || !Number.isFinite(Number(item.final_line_price)))) {
        return null;
      }
      return items.reduce((total, item) => total + Number(item.final_line_price), 0);
    }

    observeCartPrice(cart, signature) {
      const total = this.getActualOfferTotal(cart);
      this.committedCartPrice = total === null || !signature ? null : { signature, total };
      // Shipping uses the whole discounted cart, including unrelated items and
      // cart-level discounts. Keep offer-only prices for the selection display.
      this.committedCartShipping = signature && Number.isFinite(cart?.total_price)
        ? {
          signature,
          subtotal: cart.total_price,
          baseQuantity: (cart.items || []).filter(item => Number(item.variant_id || item.id) === Number(this.data.board.variantId))
            .reduce((quantity, item) => quantity + Number(item.quantity || 0), 0),
        } : null;
    }

    getCartPriceWarning(cart, expectedTotal, promotionFailed = false) {
      const actualTotal = this.getActualOfferTotal(cart);
      return promotionFailed || actualTotal === null || actualTotal > expectedTotal + 1
        ? this.data.labels.cartPriceChanged
        : '';
    }

    showResolvedCart(cartDrawer, cart, warning = '') {
      const hasSections = cartDrawer && cartDrawer.getSectionsToRender().every((section) =>
        typeof cart.sections?.[section.id] === 'string' && cart.sections[section.id].trim()
      );
      if (!hasSections) {
        window.location = window.routes.cart_url;
        return;
      }
      // Dawn replaces the drawer contents but keeps its outer custom element.
      // Clear the initial empty state before opening a newly populated cart.
      cartDrawer.classList.toggle('is-empty', cart.items.length === 0);
      cartDrawer.renderContents(cart);
      if (warning) {
        const message = document.createElement('p');
        message.className = 'flipzy-selection__status';
        message.setAttribute('role', 'alert');
        message.textContent = warning;
        cartDrawer.querySelector('.drawer__footer')?.prepend(message);
      }
    }

    async openCommittedCart(button) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        window.location = window.routes.cart_url;
        return;
      }
      cartDrawer.setActiveElement(button);
      this.clearStatus();
      this.setBusy(button, false, this.data.labels.openCart);
      try {
        const [cart, sections] = await Promise.all([this.fetchCart(), this.fetchCartSections(cartDrawer)]);
        this.committedSignature = this.getCartSelectionSignature(this.getExistingOfferItems(cart));
        this.observeCartPrice(cart, this.committedSignature);
        this.showResolvedCart(cartDrawer, { ...cart, sections });
      } catch (error) {
        window.location = window.routes.cart_url;
      } finally {
        this.clearBusy(button);
        this.render();
      }
    }

    async submitOffer(button) {
      if (this.isSubmitting) return;
      if (this.committedSignature && this.committedSignature === this.getSelectionSignature()) {
        return this.openCommittedCart(button);
      }

      const selectedPacks = this.getSelectedPacks();
      const validationMessage = this.getValidationMessage(selectedPacks);
      if (validationMessage) {
        this.showStatus(validationMessage);
        return;
      }

      const signature = this.getSelectionSignature();
      const totals = this.getTotals(this.boardQuantity, selectedPacks);
      const revision = String(Date.now());
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.setActiveElement(button);

      this.clearStatus();
      this.setBusy(button, Boolean(this.committedSignature));
      let cartWasChanged = false;
      let reloadPending = false;

      try {
        if (window.FlipzyPriceTest) await window.FlipzyPriceTest.prepareCart();
        const cart = await this.fetchCart();
        const existingOfferItems = this.getExistingOfferItems(cart);
        const isUpdating = existingOfferItems.length > 0;
        this.setText('[data-add-label]', isUpdating ? this.data.labels.updating : this.data.labels.adding, button);

        const items = this.buildCartItems(selectedPacks, totals, signature, revision);
        let parsedState;

        if (isUpdating) {
          parsedState = await this.updateOfferItems(items, cart, cartDrawer, () => { cartWasChanged = true; });
        } else {
          parsedState = await this.postCartAdd(items, cartDrawer);
          cartWasChanged = true;
        }

        parsedState = await this.resolveOfferCart(parsedState, cartDrawer);
        if (window.FlipzyPriceTest) window.FlipzyPriceTest.verifyCart(parsedState);
        const warning = this.getCartPriceWarning(parsedState, totals.total, parsedState.promotionFailed);
        this.committedSignature = signature;
        this.observeCartPrice(parsedState, signature);
        this.hasInteracted = false;
        this.showStatus(warning || (isUpdating ? this.data.labels.updated : this.data.labels.success));

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'flipzy-routine-offer',
            cartData: parsedState,
          });
        }

        this.showResolvedCart(cartDrawer, parsedState, warning);
      } catch (error) {
        // Price recovery already requested a reload. Do not reuse this page's
        // previous quote or flash an error while the native offer is loading.
        if (error.reloadRequired) { reloadPending = true; return; }
        const message = cartWasChanged ? this.data.labels.cartPriceChanged : error.message || this.data.labels.error;
        this.showStatus(message);
        if (cartWasChanged || error.uncertainCart) {
          try {
            const cart = await this.fetchCart();
            cart.sections = await this.fetchCartSections(cartDrawer);
            this.committedSignature = this.getCartSelectionSignature(this.getExistingOfferItems(cart));
            this.observeCartPrice(cart, this.committedSignature);
            this.hasInteracted = true;
            this.showResolvedCart(cartDrawer, cart, message);
          } catch (cartError) {
            window.location = window.routes.cart_url;
          }
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'flipzy-routine-offer',
            errors: error.message,
            message: error.message,
          });
        }
      } finally {
        if (!reloadPending) {
          this.clearBusy(button);
          this.render();
        }
      }
    }
  }

  customElements.define('flipzy-routine-offer', FlipzyRoutineOffer);
}
